import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { getExtendedFormInputInfoByType } from "../../../../../../../components/Booking/Maintenance/Details/types";
import { FieldViewProps } from "../../../../../../../components/Form/FieldView";
import { getDateValueInTimeZone } from "../../../../../../../components/Generic/MilestoneDates";
import {
  FieldViewWithGridProps,
  SectionView,
} from "../../../../../../../components/Generic/SideView/SectionView";
import getShipmentOptions from "../../../../../../../components/Shipments/hooks/getShipmentOptions";
import { useShipmentResourcesContext } from "../../../../../../../components/Shipments/hooks/useResourceContext";
import { useShipmentContext } from "../../../../../../../components/Shipments/hooks/useShipmentContext";
import ContainerDetails from "../../../../../../../components/Shipments/Maintenance/Details/components/Containers";
import { useAppState } from "../../../../../../../context/StateContext";
import { MilestoneType } from "../../../../../../../models/milestone";
import { Shipment } from "../../../../../../../models/shipments";
import { Shipper } from "../../../../../../../types";
import { BookingModes, ShipmentDetails, ShipperType } from "../../../../../../../util/enums";
import { ContainerViewWrapper } from "./styles";

type ShipmentViewDetailsProps = {
  shipment: Shipment | undefined;
  purchaseOrders: string;
};
export const ShipmentViewDetails: React.FC<ShipmentViewDetailsProps> = (props) => {
  const { shipment } = props;
  const { isFMorSPVersion, isFMVersion } = useAppState();
  const { t: translate } = useTranslation();
  const {
    supplierList,
    customerList,
    projectList,
    carrierList,
    milestoneDates,
    loading: loadingResources,
  } = useShipmentResourcesContext();

  const { allShipmentStatusOprions, boundOptions } = getShipmentOptions(isFMVersion);

  const { showContainerSection, hsCodes } = useShipmentContext();

  const baseViewFieldProps = (key: keyof Shipment) => {
    const props: FieldViewProps = {
      label: translate(key),
      value: shipment?.[key],
    };

    return props;
  };

  const shipperList: Shipper[] = useMemo(() => {
    const customersToShippers: Shipper[] = customerList?.map(({ id, fullName, code }) => ({
      id,
      fullName: fullName || "",
      type: ShipperType.CONSIGNEE,
      code: code || "",
    }));

    const supplierToShippers: Shipper[] = supplierList?.map(({ id, name, code }) => ({
      id,
      fullName: name || "",
      type: ShipperType.SUPPLIER,
      code: code || "",
    }));

    return [...customersToShippers, ...supplierToShippers];
  }, [supplierList, customerList]);

  const shipperSelected: Shipper | undefined = useMemo(() => {
    const { customerId, supplierId } = shipment?.shipperEntity || {};

    if (customerId) {
      return shipperList?.find(
        (shipper) => shipper?.id === customerId && shipper.type === ShipperType.CONSIGNEE
      );
    }

    if (supplierId) {
      return (
        shipperList?.find(
          (shipper) => shipper?.id === supplierId && shipper.type === ShipperType.SUPPLIER
        ) ||
        ({
          id: shipment?.shipperEntity?.supplierId,
          type: ShipperType.SUPPLIER,
          fullName: shipment?.shipperEntity?.fullName,
          code: shipment?.shipperEntity?.code,
        } as Shipper)
      );
    }
  }, [shipment?.shipperEntity, shipperList]);

  const customerSelected = isFMorSPVersion
    ? shipment?.consignee
    : customerList?.find((warehouse) => Number(shipment?.consigneeId) === Number(warehouse?.id));

  const getMilestonesFields = (type: MilestoneType) => {
    return [
      milestoneDates
        ?.filter((milestoneOption) => milestoneOption.type?.toLowerCase() === type.toLowerCase())
        .map((milestoneOption) => {
          const milestoneValue = shipment?.milestones?.find(
            (milestoneValue) => Number(milestoneValue?.milestoneId) === Number(milestoneOption?.id)
          );

          const keyName = milestoneOption.reference || milestoneOption.title;

          return {
            name: keyName,
            label: milestoneOption.title,
            value: milestoneValue?.dateValue
              ? getDateValueInTimeZone(milestoneValue?.dateValue, "dd/MM/yyyy")
              : undefined,
            loading: loadingResources,
          };
        }),
    ];
  };

  const renderLastRowFormByType = () => {
    if (!shipment?.shipmentType) return [];
    switch (shipment?.shipmentType.toLowerCase()) {
      case "air":
        return [
          {
            ...baseViewFieldProps(ShipmentDetails.PLANE),
            label: `${translate("flight")} #`,
          },
          {
            ...baseViewFieldProps(ShipmentDetails.JOURNEY),
            label: translate("airline"),
          },
          {
            ...baseViewFieldProps(ShipmentDetails.AIRWAY_BILL),
            label: `${translate("houseAirWaybill")} #`,
          },
        ];
      case "sea":
        return [
          {
            ...baseViewFieldProps(ShipmentDetails.VESSEL_NAME),
            label: translate("vessel"),
          },
          {
            ...baseViewFieldProps(ShipmentDetails.VOYAGE_NUMBER),
            label: translate("voyage"),
          },
        ];
      case "road":
      default:
        return [];
    }
  };

  const renderExtendedPortFormByType = () => {
    if (shipment?.shipmentType) {
      const info = getExtendedFormInputInfoByType(shipment?.shipmentType, translate);

      if (!info) return [];

      switch (shipment?.shipmentType) {
        case BookingModes.AIR:
        case BookingModes.SEA:
          return [
            [
              {
                ...baseViewFieldProps(ShipmentDetails.ORIGIN_LOCATION),
                label: translate("placeOfOrigin"),
                value: shipment?.[ShipmentDetails.ORIGIN_LOCATION]?.longAddress,
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_LOADING_ID),
                label: info.origin?.label,
                value: shipment?.portOfLoading?.siteCode,
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_DISCHARGE_ID),
                label: info.discharge?.label,
                value: shipment?.portOfDischarge?.siteCode,
              },
              {
                ...baseViewFieldProps(ShipmentDetails.DESTINATION_LOCATION),
                label: info.destination?.label,
                value: shipment?.[ShipmentDetails.DESTINATION_LOCATION]?.longAddress,
              },
            ],
          ];
        case BookingModes.ROAD:
        default:
          return [
            [
              {
                ...baseViewFieldProps(ShipmentDetails.ORIGIN),
                label: info.origin?.label,
                value: shipment?.portOfLoading?.siteCode,
              },
              {
                ...baseViewFieldProps(ShipmentDetails.DESTINATION),
                label: info.destination?.label,
                value: shipment?.portOfDestination?.siteCode,
              },
            ],
          ];
      }
    }

    return [];
  };

  const generalFields = [
    [
      {
        ...baseViewFieldProps(ShipmentDetails.SHIPMENT_TYPE),
        label: translate("mode"),
        value: shipment?.shipmentType ? translate(shipment.shipmentType.toLowerCase()) : "",
      },
      {
        ...baseViewFieldProps(ShipmentDetails.CONTAINER_ID),
        label: `${translate("shipment")} #`,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.LOAD_TYPE),
        label: translate("loadType"),
      },
      {
        ...baseViewFieldProps(ShipmentDetails.STATUS_SHIPMENT),
        label: translate("status"),
        value: allShipmentStatusOprions?.find(
          (status) =>
            status.value ===
            (shipment?.shipmentTracking?.shipmentStatus?.toLowerCase() ?? shipment?.statusShipment)
        )?.label,
      },
    ],
    [
      {
        ...baseViewFieldProps(ShipmentDetails.SERVICE_TYPE),
      },
      {
        ...baseViewFieldProps(ShipmentDetails.INCOTERM),
        label: translate("incoterms"),
      },
      {
        ...baseViewFieldProps(ShipmentDetails.SHIP_TERMS),
      },
    ],
    [
      {
        ...baseViewFieldProps(ShipmentDetails.COMMODITY),
        label: translate("hsCommodityCode"),
        value: hsCodes && hsCodes?.length ? hsCodes?.map((code) => code.hscode) : "",
        multipleLines: true,
      },
    ],
  ];

  const shipmentFields: FieldViewWithGridProps[][] = [
    [
      { ...baseViewFieldProps(ShipmentDetails.BOOKING_NO), label: `${translate("booking")} #` },
      {
        ...baseViewFieldProps(ShipmentDetails.MASTER_BILL),
        label: `${translate("masterbillOfLanding")} #`,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.BILL_OF_LANDING),
        label: `${translate("billOfLanding")} #`,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.HOUSE_BILL),
        label: `${translate("housebillOfLanding")} #`,
      },
    ],
    ...renderExtendedPortFormByType(),
    [
      {
        ...baseViewFieldProps(ShipmentDetails.SHIPPER_ENTITY),
        label: translate("shipper"),
        value: shipperSelected?.fullName,
        loading: loadingResources,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.SHIPPER_ENTITY),
        label: `${translate("shipperCode")} #`,
        value: shipperSelected?.code,
        loading: loadingResources,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.BOUND),
        label: translate("shipment"),
        value: boundOptions?.find((bound) => bound.value === shipment?.bound)?.label,
      },
    ],
    [
      {
        ...baseViewFieldProps(ShipmentDetails.CONSIGNEE_ID),
        label: translate("consignee"),
        value: customerSelected?.name,
      },
      {
        label: `${translate(ShipmentDetails.CONSIGNEE_CODE)} #`,
        value: customerSelected?.code,
        loading: loadingResources,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.PROJECT_ID),
        label: translate("project"),
        value: projectList?.find((project) => project.id === shipment?.projectId)?.name,
        loading: loadingResources,
      },
    ],
    [
      {
        ...baseViewFieldProps(ShipmentDetails.CARRIER),
        value: carrierList?.find((carrier) => carrier.id === shipment?.carrierId)?.name,
      },
      ...renderLastRowFormByType(),
    ],
  ];

  return (
    <div>
      <SectionView fields={generalFields} title={translate("general")} />
      <SectionView fields={shipmentFields} title={translate("shipmentDetails")} />
      {!!showContainerSection && (
        <ContainerViewWrapper>
          <ContainerDetails isView />
        </ContainerViewWrapper>
      )}
      <SectionView
        fields={getMilestonesFields(MilestoneType.ESTIMATED)}
        title={translate("estimatedDates")}
      />
      <SectionView
        fields={getMilestonesFields(MilestoneType.ACTUAL)}
        title={translate("actualDates")}
      />
    </div>
  );
};
